import React, { useState } from 'react';
import { Typography, Tooltip, Progress, Button, Alert, Tabs } from 'antd';
import { HelpCircle, Play, Upload, FileText, RotateCcw, Mic, VolumeX, Sparkles, Clock } from 'lucide-react';
import SceneAnalysisService, { AudioSegmentForUpload } from '../../../../services/sceneAnalysisService';
import type { AnalysisResponse, AnalysisResults, TranscriptSegment } from '../../../../../shared/types/analysis';
import type { Scene } from '../../../../../shared/schema';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { UpgradePrompt } from '../overlays/UpgradePrompt';
import './SceneAnalysis.scss';

const { Text } = Typography;

interface SceneAnalysisProps {
  sceneId: number;
  micEnabled: boolean;
  videoGuid?: string;
  scene?: Scene | null;
  projectId?: number;
  onUploadNeeded?: (isTemp?: boolean) => Promise<string | null>;
  onAnalysisStatusChange?: (isProcessing: boolean) => void;
  onSeekToTime?: (timeInSeconds: number) => void;
  /** For separate stream recordings: whether any audio segments exist. If false, hides the entire block. */
  hasAudioSegments?: boolean;
  /** Whether this is an unconfirmed separate stream recording (audio not uploaded yet) */
  isUnconfirmedSeparateStream?: boolean;
  /** Audio segment blobs for unconfirmed recordings - will be uploaded temporarily for analysis */
  audioSegmentBlobs?: AudioSegmentForUpload[];
}

const SceneAnalysis: React.FC<SceneAnalysisProps> = ({ 
  sceneId, 
  micEnabled, 
  videoGuid, 
  scene, 
  projectId, 
  onUploadNeeded, 
  onAnalysisStatusChange,
  onSeekToTime,
  hasAudioSegments,
  isUnconfirmedSeparateStream,
  audioSegmentBlobs
}) => {
  const [status, setStatus] = useState<'pending' | 'processing' | 'ready' | 'failed'>('pending');
  const [data, setData] = useState<AnalysisResults | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState({ step: 'uploading' as 'uploading' | 'transcribing' | 'analyzing', percentage: 0 });
  const lastProgressRef = React.useRef(0);

  const updateProgress = React.useCallback((step: 'uploading' | 'transcribing' | 'analyzing', stepPercentage: number) => {
    const ranges = { uploading: [0, 25], transcribing: [25, 65], analyzing: [65, 95] } as const;
    const [rangeStart, rangeEnd] = ranges[step];
    const overall = Math.round(rangeStart + (stepPercentage / 100) * (rangeEnd - rangeStart));
    const clamped = Math.max(overall, lastProgressRef.current);
    lastProgressRef.current = clamped;
    setProgress({ step, percentage: clamped });
  }, []);
  const [activeTab, setActiveTab] = useState<string>('analysis');
  const [activeSegmentId, setActiveSegmentId] = useState<number | null>(null);
  
  // Premium feature gating for AI scene analysis
  const { isPaid } = useFeatureAccess();
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);

  // Ref to hold callback without causing re-renders
  const onAnalysisStatusChangeRef = React.useRef(onAnalysisStatusChange);
  React.useEffect(() => {
    onAnalysisStatusChangeRef.current = onAnalysisStatusChange;
  }, [onAnalysisStatusChange]);

  React.useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await SceneAnalysisService.getAnalysis(sceneId);
        setStatus(response.status);
        // Use ref to avoid dependency on callback
        onAnalysisStatusChangeRef.current?.(response.status === 'processing');
        
        if (response.status === 'ready') {
          setData(response.results);
        } else if (response.status === 'failed') {
          setError(response.error.message || 'Analysis failed');
        } else if (response.status === 'processing' && response.progress) {
          updateProgress(response.progress.step, response.progress.percentage);
        }
      } catch (err: any) {
        console.error('[SceneAnalysis] Error fetching status:', err);
      }
    };

    // Fetch status for:
    // 1. Legacy recordings with videoUrl and micEnabled
    // 2. Separate stream recordings (analysis may have been run with temp audio)
    if ((scene?.videoUrl && micEnabled) || hasAudioSegments) {
      fetchStatus();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sceneId, scene?.videoUrl, micEnabled, hasAudioSegments]);

  const handleStartAnalysis = async () => {
    if (!isPaid) {
      setShowUpgradePrompt(true);
      return;
    }
    
    setLoading(true);
    setError(null);
    lastProgressRef.current = 0;
    updateProgress('uploading', 0);
    onAnalysisStatusChange?.(true);

    console.log('[SceneAnalysis] handleStartAnalysis called:', {
      isUnconfirmedSeparateStream,
      audioSegmentBlobsLength: audioSegmentBlobs?.length,
      audioSegmentBlobs: audioSegmentBlobs?.map(seg => ({
        hasBlob: !!seg.blob,
        blobSize: seg.blob?.size,
        hasBlobUrl: !!seg.blobUrl,
        segmentIndex: seg.segmentIndex,
      })),
      sceneVideoUrl: scene?.videoUrl,
      micEnabled,
    });

    try {
      let videoUrl = scene?.videoUrl;
      let uploadedAudioSegments: any[] | undefined;
      
      if (isUnconfirmedSeparateStream && audioSegmentBlobs && audioSegmentBlobs.length > 0) {
        const validSegments = audioSegmentBlobs.filter(
          (seg) => (seg.blob && seg.blob.size > 0) || seg.blobUrl
        );
        
        if (validSegments.length === 0) {
          throw new Error('No valid audio segments found. Please re-record the scene.');
        }
        
        console.log(`[SceneAnalysis] Uploading ${validSegments.length} temp audio segments for unconfirmed recording...`);
        updateProgress('uploading', 30);
        
        uploadedAudioSegments = await SceneAnalysisService.uploadTempAudioSegments(sceneId, validSegments);
        console.log('[SceneAnalysis] Uploaded', uploadedAudioSegments.length, 'temp audio segments');
        updateProgress('uploading', 100);
      } else if (!videoUrl && onUploadNeeded) {
        updateProgress('uploading', 10);
        videoUrl = await onUploadNeeded(true);
        updateProgress('uploading', 100);
      } else {
        updateProgress('uploading', 100);
      }
      
      const metadata = typeof scene?.metadata === 'string' 
        ? JSON.parse(scene.metadata) 
        : scene?.metadata;
      const hasAudioInMetadata = metadata?.streams?.audio?.segments?.some(
        (seg: any) => seg.key && seg.uploadStatus === 'completed'
      );
      
      if (!uploadedAudioSegments && !videoUrl && !hasAudioInMetadata) {
        throw new Error('No audio available for analysis');
      }

      updateProgress('transcribing', 0);
      await SceneAnalysisService.startAnalysis(sceneId, videoUrl || undefined, micEnabled, {
        audioSegments: uploadedAudioSegments,
      });
      setStatus('processing');

      const results = await SceneAnalysisService.pollUntilComplete(
        sceneId,
        (response: AnalysisResponse) => {
          if (response.status === 'processing' && response.progress) {
            updateProgress(response.progress.step, response.progress.percentage);
          }
        }
      );

      lastProgressRef.current = 100;
      setProgress({ step: 'analyzing', percentage: 100 });
      setData(results);
      setStatus('ready');
      onAnalysisStatusChange?.(false);
    } catch (err: any) {
      console.error('[SceneAnalysis] Analysis failed:', err);
      setError(err.message || 'Analysis failed');
      setStatus('failed');
      onAnalysisStatusChange?.(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSegmentClick = (segment: TranscriptSegment) => {
    setActiveSegmentId(segment.id);
    if (onSeekToTime) {
      onSeekToTime(segment.start);
    }
  };

  const formatTimestamp = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getSpeakingPacePercent = (wpm: number, min: number, max: number): number => {
    const percent = ((wpm - min) / (max - min)) * 100;
    return Math.max(0, Math.min(100, percent));
  };

  const getGradeColor = (grade: string): string => {
    switch (grade) {
      case 'A': return '#22c55e';
      case 'B': return '#84cc16';
      case 'C': return '#eab308';
      case 'D': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getGradeBgColor = (grade: string): string => {
    switch (grade) {
      case 'A': return 'rgba(34, 197, 94, 0.1)';
      case 'B': return 'rgba(132, 204, 22, 0.1)';
      case 'C': return 'rgba(234, 179, 8, 0.1)';
      case 'D': return 'rgba(239, 68, 68, 0.1)';
      default: return 'rgba(107, 114, 128, 0.1)';
    }
  };

  const getEnergyColor = (level: string): string => {
    switch (level) {
      case 'High': return '#22c55e';
      case 'Medium': return '#eab308';
      case 'Low': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getEnergyBgColor = (level: string): string => {
    switch (level) {
      case 'High': return 'rgba(34, 197, 94, 0.1)';
      case 'Medium': return 'rgba(234, 179, 8, 0.1)';
      case 'Low': return 'rgba(239, 68, 68, 0.1)';
      default: return 'rgba(107, 114, 128, 0.1)';
    }
  };

  const getProgressSteps = () => {
    const steps = [
      { key: 'uploading', label: 'Preparing', icon: Upload },
      { key: 'transcribing', label: 'Transcribing', icon: FileText },
      { key: 'analyzing', label: 'Analyzing', icon: Sparkles },
    ];
    
    const currentIndex = steps.findIndex(s => s.key === progress.step);
    
    return steps.map((step, index) => ({
      ...step,
      status: index < currentIndex ? 'completed' : index === currentIndex ? 'active' : 'pending'
    }));
  };

  const renderAnalysisTab = () => (
    <>
      <div className="analysis-results-grid">
        <div className="result-card">
          <div className="result-header">
            <Text className="result-label">Speaking Pace</Text>
            <Tooltip title="Optimal speaking pace is 120-180 WPM. Too slow may bore viewers, too fast may be hard to follow.">
              <HelpCircle size={14} className="result-help" />
            </Tooltip>
          </div>
          <div className="pace-gauge">
            <div className="pace-bar">
              <div 
                className="pace-marker" 
                style={{ left: `${getSpeakingPacePercent(data!.speakingPace.wpm, data!.speakingPace.min, data!.speakingPace.max)}%` }}
              />
            </div>
            <div className="pace-labels">
              <span>Slow</span>
              <span>Optimal</span>
              <span>Fast</span>
            </div>
          </div>
          <div className="pace-value">{data!.speakingPace.wpm} <span>WPM</span></div>
        </div>

        <div className="result-card">
          <div className="result-header">
            <Text className="result-label">Filler Words</Text>
            <Tooltip title="Count of filler words (um, uh, like, you know) compared to average. Lower is better.">
              <HelpCircle size={14} className="result-help" />
            </Tooltip>
          </div>
          <div className="filler-content">
            <div className="filler-stats">
              <span className="filler-count">{data!.fillerWords.count}</span>
              <span className="filler-avg">vs avg {data!.fillerWords.average}</span>
            </div>
            <div 
              className="filler-grade"
              style={{ 
                backgroundColor: getGradeBgColor(data!.fillerWords.grade),
                color: getGradeColor(data!.fillerWords.grade)
              }}
            >
              {data!.fillerWords.grade}
            </div>
          </div>
        </div>

        <div className="result-card">
          <div className="result-header">
            <Text className="result-label">Energy Level</Text>
            <Tooltip title="Measured by vocal dynamics and pacing variation. Higher energy increases viewer engagement.">
              <HelpCircle size={14} className="result-help" />
            </Tooltip>
          </div>
          <div 
            className="energy-badge"
            style={{ 
              backgroundColor: getEnergyBgColor(data!.energyLevel),
              color: getEnergyColor(data!.energyLevel)
            }}
          >
            {data!.energyLevel}
          </div>
        </div>

        <div className="result-card">
          <div className="result-header">
            <Text className="result-label">Confidence</Text>
            <Tooltip title="Calculated from vocal steadiness, pace consistency, and pause patterns. 0-100 score.">
              <HelpCircle size={14} className="result-help" />
            </Tooltip>
          </div>
          <div className="confidence-content">
            <Progress 
              percent={data!.confidence} 
              strokeColor="#4A3CFA"
              trailColor="#E5E7EB"
              size={{ height: 8 }}
              format={(percent) => `${percent}%`}
              strokeLinecap="round"
            />
          </div>
        </div>

        <div className="result-card">
          <div className="result-header">
            <Text className="result-label">Engagement Hooks</Text>
            <Tooltip title="Attention-grabbing statements detected: stories, facts, or calls-to-action.">
              <HelpCircle size={14} className="result-help" />
            </Tooltip>
          </div>
          <div className="metric-value">{data!.engagementHooks}</div>
        </div>

        <div className="result-card">
          <div className="result-header">
            <Text className="result-label">Questions Asked</Text>
            <Tooltip title="Questions encourage active thinking and increase audience engagement.">
              <HelpCircle size={14} className="result-help" />
            </Tooltip>
          </div>
          <div className="metric-value">{data!.questionsAsked}</div>
        </div>
      </div>

      <div className="analysis-actions">
        <Button 
          type="text" 
          icon={<RotateCcw size={14} />} 
          onClick={handleStartAnalysis}
          loading={loading}
          className="rerun-button"
        >
          Re-run Analysis
        </Button>
      </div>
    </>
  );

  const renderTranscriptTab = () => {
    const segments = data?.transcript?.segments || [];
    
    // Count filler words from all segments
    const fillerWordCount = segments.reduce((count, segment) => {
      return count + segment.words.filter(word => word.isFiller).length;
    }, 0);

    return (
      <div className="transcript-container">
        <div className="transcript-header">
          <div className="header-info">
            <Text type="secondary" className="word-count">
              {fillerWordCount} {fillerWordCount === 1 ? 'word' : 'words'}
            </Text>
            <Text type="secondary" className="duration">
              {formatTimestamp(data!.transcript.duration)}
            </Text>
          </div>
          <div className="filler-legend">
            <span className="legend-item">
              <span className="filler-indicator" />
              Filler word
            </span>
          </div>
        </div>

        <div className="transcript-segments">
          {segments.map((segment) => (
            <div
              key={segment.id}
              className={`transcript-segment ${activeSegmentId === segment.id ? 'active' : ''} ${onSeekToTime ? 'clickable' : ''}`}
              onClick={() => handleSegmentClick(segment)}
            >
              <div className="segment-timestamp">
                <Clock size={12} />
                <span>{formatTimestamp(segment.start)}</span>
              </div>
              <div className="segment-text">
                {segment.words.map((word, wordIndex) => (
                  <span 
                    key={wordIndex}
                    className={word.isFiller ? 'filler-word' : ''}
                  >
                    {word.word}{' '}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Hide entire block if hasAudioSegments is explicitly false (no audio segments recorded)
  // This is for separate stream recordings where mic was never enabled
  if (hasAudioSegments === false) {
    return null;
  }


  // Hide "Analysis Unavailable" block for non-paid users - only show for paid users
  // Non-paid users always see the "Analyze Your Delivery" section (triggers upgrade modal when clicked)
  if (!micEnabled && isPaid) {
    return (
      <div className="scene-analysis-wrapper">
        <div className="analysis-empty-state">
          <div className="empty-icon-wrapper">
            <Mic size={24} />
          </div>
          <Text strong className="empty-title">Analysis Unavailable</Text>
          <Text type="secondary" className="empty-description">
            Microphone was disabled during recording. Scene analysis requires audio to analyze speaking performance.
          </Text>
        </div>
      </div>
    );
  }
  
  // For non-paid users with mic disabled, still show the analyze section
  // Clicking will trigger upgrade modal - always show the feature to encourage upgrades

  if (status === 'pending') {
    return (
      <>
        <div className="scene-analysis-wrapper">
          <div className="analysis-pending-state">
            <div className="pending-content">
              <div className="pending-icon-wrapper">
                <Sparkles size={28} fill="#4A3CFA" />
              </div>
              <div className="pending-text">
                <Text strong className="pending-title">Analyze Your Delivery</Text>
                <Text type="secondary" className="pending-description">
                  Get insights on speaking pace, filler words, energy, and confidence
                </Text>
              </div>
            </div>
            <Button
              type="primary"
              icon={<Play size={16} />}
              onClick={handleStartAnalysis}
              loading={loading}
              className="analyze-button"
            >
              {loading ? 'Analyzing...' : 'Analyze Scene'}
            </Button>
          </div>

          <div className="analysis-preview-grid">
            <div className="preview-metric">
              <Text type="secondary" className="preview-label">Speaking Pace</Text>
              <div className="preview-value blurred">135 WPM</div>
            </div>
            <div className="preview-metric">
              <Text type="secondary" className="preview-label">Filler Words</Text>
              <div className="preview-value blurred">8</div>
            </div>
            <div className="preview-metric">
              <Text type="secondary" className="preview-label">Energy Level</Text>
              <div className="preview-value blurred">High</div>
            </div>
            <div className="preview-metric">
              <Text type="secondary" className="preview-label">Confidence</Text>
              <div className="preview-value blurred">75%</div>
            </div>
          </div>
        </div>
        
        <UpgradePrompt
          visible={showUpgradePrompt}
          onClose={() => setShowUpgradePrompt(false)}
          feature="AI Scene Analysis"
          description="Upgrade to Pro to unlock AI-powered scene analysis. Get detailed insights on speaking pace, filler words, energy level, and more to improve your recordings."
        />
      </>
    );
  }

  if (status === 'processing') {
    const steps = getProgressSteps();
    
    return (
      <div className="scene-analysis-wrapper">
        <div className="analysis-processing-state">
          <div className="processing-steps">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <React.Fragment key={step.key}>
                  <div className={`processing-step ${step.status}`}>
                    <div className="step-icon">
                      <Icon size={18} />
                    </div>
                    <Text className="step-label">{step.label}</Text>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`step-connector ${steps[index + 1].status === 'pending' ? '' : 'active'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
          <Progress
            percent={progress.percentage}
            strokeColor="#4A3CFA"
            trailColor="#E5E7EB"
            size={{ height: 6 }}
            showInfo={false}
            className="processing-progress"
          />
          <Text type="secondary" className="processing-time">Usually takes 20-30 seconds</Text>
        </div>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="scene-analysis-wrapper">
        <Alert
          message="Analysis Failed"
          description={error || 'An error occurred during analysis. Please try again.'}
          type="error"
          showIcon
          action={
            <Button size="small" onClick={handleStartAnalysis} loading={loading} icon={<RotateCcw size={14} />}>
              Retry
            </Button>
          }
          className="analysis-error-alert"
        />
      </div>
    );
  }

  if (!data) return null;

  if (data.speakingPace.wpm === 0) {
    return (
      <div className="scene-analysis-wrapper">
        <div className="analysis-empty-state">
          <div className="empty-icon-wrapper">
            <VolumeX size={24} />
          </div>
          <Text strong className="empty-title">No Speech Detected</Text>
          <Text type="secondary" className="empty-description">
            The analysis completed but didn't detect any speech in this recording.
          </Text>
        </div>
      </div>
    );
  }

  const hasTranscript = data.transcript?.segments && data.transcript.segments.length > 0;

  return (
    <>
      <div className="scene-analysis-wrapper">
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          className="analysis-tabs"
          items={[
            {
              key: 'analysis',
              label: 'Analysis',
              children: renderAnalysisTab(),
            },
            ...(hasTranscript ? [{
              key: 'transcript',
              label: 'Transcript',
              children: renderTranscriptTab(),
            }] : []),
          ]}
        />
      </div>
      
      <UpgradePrompt
        visible={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        feature="AI Scene Analysis"
        description="Upgrade to Pro to unlock AI-powered scene analysis. Get detailed insights on speaking pace, filler words, energy level, and more to improve your recordings."
      />
    </>
  );
};

export default React.memo(SceneAnalysis);
